import chunk from 'lodash-es/chunk';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import * as colors from '../../shared/colors';
import {
    addDays, addMonths, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subMonths
} from '../../shared/dateFns';
import t from '../../shared/translations';
import { calendarSelectMonth, Dispatch, State } from '../state';
import Day from './Day';

const weeksSelector = createSelector(
    (state: State) => state.calendar.selectedMonth,
    (date) => {
        const firstDate = startOfWeek(startOfMonth(date));
        const lastDate = endOfWeek(endOfMonth(date));
        const dateRange: Date[] = [];
        for (let i = firstDate, iMax = lastDate; i <= iMax; i = addDays(i, 1)) {
            dateRange.push(i);
        }
        return chunk(dateRange, 7);
    },
);

interface ArrowProps extends React.SVGProps<SVGSVGElement> {
    flip?: boolean;
}

const Arrow = ({ flip, ...props }: ArrowProps) => (
    <svg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg' {...props}>
        <circle cx='50' cy='50' r='47' />
        <path d={`M${flip ? '40' : '60'} 15 L${flip ? '70' : '30'} 50 L${flip ? '40' : '60'} 85`} />
    </svg>
);

const Wrapper = styled.div`
    margin-bottom: 1rem;
`;

const MonthRow = styled.div`
    border: 1px solid ${colors.text};
    border-radius: 8px;
`;

const MonthHeader = styled(MonthRow)`
    display: flex;
    padding: 10px 2.7vw;
    align-items: center;

    & > svg {
        cursor: pointer;
        width: 1.8rem;
        height: 1.8rem;
        fill: none;
        stroke: ${colors.text};
        stroke-width: 4.5;
    }
`;

const MonthHeading = styled.span`
    font-size: 1.5rem;
    text-align: center;
    flex-grow: 1;
`;

const WeekHeading = styled(MonthRow)`
    margin-top: -1px;
    display: flex;
    padding: 1px 2.5vw;
    font-size: 0.9rem;
    justify-content: space-between;
    & > * {
        text-align: center;
        width: 2rem;
    }
`;

const Week = styled(MonthRow)`
    padding: 0 2.5vw;
    margin-top: -1px;
    display: flex;
    justify-content: space-between;
`;

function Month(): JSX.Element {
    const dispatch = useDispatch<Dispatch>();
    const month = useSelector((state: State) => state.calendar.selectedMonth);
    const weeks = useSelector(weeksSelector);

    const prev = () => {
        const newMonth = subMonths(month, 1);
        dispatch(calendarSelectMonth(newMonth));
    };
    const next = () => {
        const newMonth = addMonths(month, 1);
        dispatch(calendarSelectMonth(newMonth));
    };

    return <Wrapper>
        <MonthHeader>
            <Arrow onClick={prev} />
            <MonthHeading>{format(month, 'yyyy-MM')}</MonthHeading>
            <Arrow flip onClick={next} />
        </MonthHeader>
        <WeekHeading>
            <span>{t('player.calendar.label-monday')}</span>
            <span>{t('player.calendar.label-tuesday')}</span>
            <span>{t('player.calendar.label-wednesday')}</span>
            <span>{t('player.calendar.label-thursday')}</span>
            <span>{t('player.calendar.label-friday')}</span>
            <span>{t('player.calendar.label-saturday')}</span>
            <span>{t('player.calendar.label-sunday')}</span>
        </WeekHeading>
        <div>
            {weeks.map((week, idx) => (
                <Week key={idx}>
                    {week.map((day, idx) => (
                        <Day key={idx} day={day} />
                    ))}
                </Week>
            ))}
        </div>
    </Wrapper>;
}

export default React.memo(Month);
